// 家用面板（ls-loop docs/panel）：觀看 App 的加入（V-01）、移除（V-16）、清單顯示（TP1a.10）。
// API：docs/panel/03-sd.md §2.3.1（/v3/panel-activations*）、§2.2 撤銷（DELETE /v3/panels/{id}）、§4（settings）。
import { ApiError, apiFetch, tokenStore } from '../../lib/api';
import { config } from '../../config';
import { isCameraClass, isPanelDevice, type DeviceSummary } from './deviceModel';

export const PANEL_MODEL = 'home-panel';

export interface PanelListInfo {
  online?: boolean;
  /** µs */
  last_contact_at?: number;
  photo_count?: number;
  needs_rejoin?: boolean;
}

export { isPanelDevice as isPanel } from './deviceModel';

/** 面板 QR（`https://<網域>/panel-join#a=<activation_id>`）或短碼（8 碼，可帶 -）。不是面板的碼回 null。 */
export type PanelJoinInput = { activation_id: string } | { short_code: string };

const SHORT_CODE = /^[A-HJ-KM-NP-Z2-9]{8}$/; // 排除 0 O 1 I L（docs/mycam/06 §0 同一套）

export function parsePanelJoinInput(raw: string): PanelJoinInput | null {
  const s = raw.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      if (u.pathname.replace(/\/+$/, '') !== '/panel-join') return null;
      const a = new URLSearchParams(u.hash.replace(/^#/, '')).get('a');
      return a && /^[0-9a-f]{16,64}$/i.test(a) ? { activation_id: a.toLowerCase() } : null;
    } catch {
      return null;
    }
  }
  const code = s.replace(/[\s-]/g, '').toUpperCase();
  return SHORT_CODE.test(code) ? { short_code: code } : null;
}

export interface PanelClaim {
  /** 申請代號 pa_…（之後 approve / reject 用；不是 QR 上那串） */
  activation_id: string;
  match_code: string;
  model_name?: string;
  expires_at?: number;
  rejoin_candidate?: { panel_device_id: string; name: string; reason?: string };
  /** 這位主人可被取代的面板（SD §2.3a 主人指定取代；rejoin 那台排第一、is_rejoin）。舊雲端沒有這欄 */
  replace_candidates?: PanelReplaceCandidate[];
}

export interface PanelReplaceCandidate {
  panel_device_id: string;
  name: string;
  /** µs；0 = 從沒聯絡過 */
  last_contact_at: number;
  online: boolean;
  photo_count: number;
  is_rejoin?: boolean;
}

/** 可取代清單：雲端有給就用；舊雲端只有 rejoin_candidate → 補成一筆 */
export function replaceCandidatesOf(c: PanelClaim): PanelReplaceCandidate[] {
  if (c.replace_candidates) return c.replace_candidates;
  const r = c.rejoin_candidate;
  return r ? [{ panel_device_id: r.panel_device_id, name: r.name, last_contact_at: 0, online: false, photo_count: 0, is_rejoin: true }] : [];
}

export interface PanelApproveOptions {
  keep?: boolean;
  /** 主人指定取代哪一台（panel device id）；帶了一定是 keep=true */
  replaceOf?: string;
}

export interface PanelApproveResult {
  state: string;
  panel_device_id: string;
  rejoined?: boolean;
  replaced?: boolean;
}

export async function claimPanel(input: PanelJoinInput): Promise<PanelClaim> {
  return (await apiFetch<PanelClaim>('/v3/panel-activations:claim', { method: 'POST', body: input })).data;
}

export async function approvePanel(handle: string, name: string, opts: PanelApproveOptions = {}): Promise<PanelApproveResult> {
  const body: Record<string, unknown> = { name };
  if (opts.replaceOf) {
    body.keep = true;
    body.replace_of = opts.replaceOf;
  } else if (opts.keep !== undefined) body.keep = opts.keep;
  return (await apiFetch<PanelApproveResult>(
    `/v3/panel-activations/${encodeURIComponent(handle)}/approve`, { method: 'POST', body })).data;
}

export async function rejectPanel(handle: string): Promise<void> {
  await apiFetch(`/v3/panel-activations/${encodeURIComponent(handle)}/reject`, { method: 'POST', body: {} });
}

export async function removePanel(panelDeviceId: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(panelDeviceId)}`, { method: 'DELETE' });
}

/** V-22 最小版：核准後門鈴預設全勾（John 決定 1）。完整快速設定是 TP4.9。 */
export async function prefillPanelDoorbells(panelDeviceId: string, doorbellIds: string[]): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(panelDeviceId)}/settings`, { method: 'PUT', body: { doorbells: doorbellIds } });
}

/** 主人的門鈴（有 0x8030 的裝置；面板本身不算） */
export function doorbellIdsOf(devices: DeviceSummary[]): string[] {
  return devices
    .filter((d) => d.role === 'owner' && !isPanelDevice(d) && (d.capability_effective ?? []).some((c) => (c.cluster ?? '').toLowerCase() === '0x8030'))
    .map((d) => d.device_id);
}

// ---- P1b：V-07 這台能做什麼、V-08 常用地點、V-09 新地點核准（docs/panel 03-sd §2.4 / §15，05-ui-ux V-07–V-09）----

/** 面板上傳的能力表（SD §15.1；只列觀看 App 用到的欄位） */
export interface PanelCaps {
  model?: string;
  os?: string;
  app_version?: string;
  measured_at?: string;
  biometric?: { available?: boolean; strong?: boolean; kind?: string; passcode_set?: boolean; key_registered?: boolean };
  ble_advertise?: boolean;
  /** BLE 廣播能力與原因（10-phone-auth-scenes §11）；舊版面板沒有 → 從 ble_advertise / pending 推 */
  ble?: { advertise?: boolean; reason?: string | null; tested_at?: string; error_code?: number | string };
  wifi_info?: string;
  audio?: { speaker?: boolean; mic?: boolean; duplex?: boolean; permission?: string };
  front_camera?: { available?: boolean };
  screen?: { w?: number; h?: number; inch?: number };
  memory_mb?: number;
  storage_free_mb?: number;
  /** unlock_phone = 用手機確認（掃 QR）；unlock_phone_ble = 手機靠近確認（藍牙） */
  features?: { unlock_biometric?: boolean; unlock_phone?: boolean; unlock_phone_ble?: boolean; intercom?: string; photo_long_edge?: number };
  pending?: string[];
  diag_failed?: boolean;
}

export interface PanelDegrade {
  level: number;
  since?: number;
  observe_until?: number;
  locked?: boolean;
}

export interface PanelCapsView {
  caps: PanelCaps | null;
  caps_at?: number;
  degrade?: PanelDegrade;
  safe_mode?: boolean;
}

export async function getPanelCaps(id: string): Promise<PanelCapsView> {
  return (await apiFetch<PanelCapsView>(`/v3/panels/${encodeURIComponent(id)}/caps`)).data;
}

/** 重新診斷；restore=true 時清降級並進 3 天觀察期（鎖住 → 409 DEGRADE_LOCKED） */
export async function rediagnosePanel(id: string, restore: boolean): Promise<PanelDegrade> {
  return (await apiFetch<{ degrade: PanelDegrade }>(`/v3/panels/${encodeURIComponent(id)}/caps:rediagnose`, { method: 'POST', body: { restore } })).data.degrade;
}

export async function unlockPanelDegrade(id: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/degrade:unlock`, { method: 'POST', body: {} });
}

export async function exitPanelSafeMode(id: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/safemode:exit`, { method: 'POST', body: {} });
}

export async function requestPanelReport(id: string): Promise<string> {
  return (await apiFetch<{ req_id: string }>(`/v3/panels/${encodeURIComponent(id)}/report:request`, { method: 'POST', body: {} })).data.req_id;
}

/** V-07 白話列表（同面板 P-05 CapLines；只列名稱 + 原因，原始值放［詳細］） */
export interface CapLine {
  mark: 'ok' | 'no' | 'partial';
  key: string;
}

/** 「手機靠近確認」那一列（同 Android lscore / LSCoreKit PanelPhoneBle、面板 P-05；10-phone-auth-scenes §11）：
 *  no_adv（硬體做不到）/ bt_off（藍牙關著，不算硬體做不到）/ denied（沒允許）/ null（不顯示：可以用、或權限還沒問） */
export function phoneBleLine(c: PanelCaps): 'no_adv' | 'bt_off' | 'denied' | null {
  const adv = c.ble?.advertise ?? c.ble_advertise ?? false;
  const unlockBle = c.features?.unlock_phone_ble ?? adv;
  if (adv || unlockBle) return null;
  const reason = c.ble?.reason ?? ((c.pending ?? []).includes('bluetooth') ? 'permission_pending' : 'no_multi_adv');
  if (reason === 'bt_off') return 'bt_off';
  if (reason === 'permission_denied') return 'denied';
  if (reason === 'permission_pending') return null;
  return 'no_adv';
}

export function capLines(c: PanelCaps | null, safeMode: boolean): CapLine[] {
  if (safeMode) return [{ mark: 'partial', key: 'panels.cap.safeMode' }];
  if (!c) return [{ mark: 'partial', key: 'panels.cap.diagnosing' }];
  const out: CapLine[] = [{ mark: 'ok', key: 'panels.cap.basic' }];
  const ic = c.features?.intercom;
  out.push(ic === 'duplex' ? { mark: 'ok', key: 'panels.cap.talkFull' }
    : ic === 'listen' ? { mark: 'partial', key: 'panels.cap.talkListenOnly' }
    : { mark: 'no', key: 'panels.cap.talkNone' });
  out.push({ mark: 'ok', key: 'panels.cap.unlockPin' });
  if (!c.features?.unlock_biometric) out.push({ mark: 'no', key: 'panels.cap.fingerprintOff' });
  out.push(c.wifi_info === 'granted' ? { mark: 'ok', key: 'panels.cap.homeWifi' } : { mark: 'partial', key: 'panels.cap.homeAsnOnly' });
  if (c.features?.unlock_phone) out.push({ mark: 'ok', key: 'panels.cap.phoneQrOk' });
  const ble = phoneBleLine(c);
  if (ble === 'no_adv') out.push({ mark: 'no', key: 'panels.cap.phoneBleNoAdv' });
  if (ble === 'bt_off') out.push({ mark: 'partial', key: 'panels.cap.phoneBleBtOff' });
  if (ble === 'denied') out.push({ mark: 'no', key: 'panels.cap.phoneBleDenied' });
  return out;
}

export interface PanelNetwork {
  id: string;
  kind: 'home' | 'permanent' | 'hours' | string;
  has_wifi: boolean;
  bssid_count: number;
  org?: string;
  asn?: number;
  prefix: string;
  cities: string[];
  expires_at?: number;
  ring?: boolean;
  added_at: number;
}

export interface PanelPendingLocation {
  org?: string;
  city?: string;
  from_org?: string;
  reason: string;
  since: number;
  can_once: boolean;
}

export interface PanelNetworksView {
  networks: PanelNetwork[];
  pending?: PanelPendingLocation;
  city_notice?: { net_id: string; city: string; from?: string; at: number };
}

export async function getPanelNetworks(id: string): Promise<PanelNetworksView> {
  return (await apiFetch<PanelNetworksView>(`/v3/panels/${encodeURIComponent(id)}/networks`)).data;
}

export type LocationMode = 'once' | 'hours' | 'permanent' | 'remove';

export async function approvePanelLocation(id: string, mode: LocationMode, hours?: number, ringDuring?: boolean): Promise<void> {
  const body: Record<string, unknown> = { mode };
  if (mode === 'hours') body.hours = hours ?? 4;
  if (ringDuring !== undefined) body.ring_during = ringDuring;
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/location:approve`, { method: 'POST', body });
}

export async function rememberPanelCity(id: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/location:remember-city`, { method: 'POST', body: {} });
}

export async function deletePanelNetwork(id: string, nid: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/networks/${encodeURIComponent(nid)}`, { method: 'DELETE' });
}

/** 「家裡：中華電信・Wi-Fi・台北」「暫時：遠傳・到 22:00」的組成（文案由呼叫端套 i18n） */
export function networkSummary(n: PanelNetwork): { kindKey: string; parts: string[]; until?: number } {
  const kindKey = n.kind === 'hours' ? 'panels.net.kindTemp' : n.kind === 'permanent' ? 'panels.net.kindPermanent' : 'panels.net.kindHome';
  const parts = [n.org || n.prefix, ...(n.cities.length ? [n.cities.join('、')] : [])];
  return { kindKey, parts, until: n.kind === 'hours' ? n.expires_at : undefined };
}

/** 推播 data.type → 要不要帶去面板頁（V-09 / V-13；panel.* 都帶 panel_device_id） */
export function panelPushTarget(data: Record<string, unknown>): string | null {
  const t = String(data.type ?? '');
  // 轉到手機（V-14 P6）：進門口即時影像，再一鍵［接聽］（不直接接聽）
  if (t === 'panel.doorbell_handoff') {
    return doorbellLinkTarget(String(data.deep_link ?? '')) ?? doorbellLiveTarget(String(data.device_id ?? ''), String(data.ring_id ?? ''), String(data.handoff_id ?? ''));
  }
  const id = String(data.panel_device_id ?? '');
  if (!t.startsWith('panel.') || !id) return null;
  // 行事曆（09 §7.2）：到行事曆那一件；ask_family 另開編輯並預填長輩說的新時間 / 新標題（同 panelCalNotice.noticeTarget）
  if (t.startsWith('panel.calendar_')) {
    const base = `/devices/${encodeURIComponent(id)}/calendar`;
    const ev = String(data.event_id ?? '');
    if (!ev) return base;
    const q = new URLSearchParams({ event: ev });
    if (data.recurrence_id) q.set('rid', String(data.recurrence_id));
    if (t === 'panel.calendar_ask_family') {
      if (String(data.ask_op ?? data.op ?? '') === 'delete' || data.variant === 'ask_delete') q.set('del', '1'); // 刪除確認（09 §9.10）
      else {
        q.set('edit', '1');
        if (data.new_start) q.set('new_start', String(data.new_start));
        if (data.new_title) q.set('new_title', String(data.new_title));
      }
    }
    return `${base}?${q.toString()}`;
  }
  // 版面被改（07-dashboard §7）：捲到版面卡片，方便［還原這一版］
  return `/devices/${encodeURIComponent(id)}${t === 'panel.layout_changed' ? `#${LAYOUT_CARD_ANCHOR}` : ''}`;
}

/** 門口即時影像頁（帶這一通的 ring_id / handoff_id；V-14） */
export function doorbellLiveTarget(deviceId: string, ringId?: string, handoffId?: string): string | null {
  if (!deviceId) return null;
  const q = new URLSearchParams();
  if (ringId) q.set('ring_id', ringId);
  if (handoffId) q.set('handoff_id', handoffId);
  const qs = q.toString();
  return `/devices/${encodeURIComponent(deviceId)}/live${qs ? `?${qs}` : ''}`;
}

/** `lscloud://doorbell/{dev}?ring_id=&handoff_id=` → 網頁路由；不是門鈴連結回 null */
export function doorbellLinkTarget(link: string): string | null {
  const m = /^lscloud:\/\/doorbell\/([^/?#]+)(?:\?([^#]*))?$/.exec(link.trim());
  if (!m) return null;
  const q = new URLSearchParams(m[2] ?? '');
  return doorbellLiveTarget(decodeURIComponent(m[1]), q.get('ring_id') ?? '', q.get('handoff_id') ?? '');
}


// ---- P2 / P3：V-03 相簿、V-04 輪播與顯示設定（docs/panel 03-sd §3 / §4 v1.3、05-ui-ux V-03 / V-04）----

export const PANEL_PHOTO_QUOTA = 100;
/** 沒有能力表（或不是主人讀不到）時的照片長邊：iPad mini 6（SD §3.2） */
export const DEFAULT_LONG_EDGE = 2266;
/** 單張上限 10 MB（SD §3.2 第 4 步） */
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

export interface PanelAlbumPhoto {
  photo_id: string;
  url: string;
  w: number;
  h: number;
  sha256: string;
  bytes?: number;
  uploaded_by?: string;
  created_at?: number;
}
export interface PanelAlbum { version: string; photos: PanelAlbumPhoto[]; count: number; quota: number }
export interface PanelPhotoUpload { photo_id: string; upload_url: string; expires_at?: number }
export interface PanelCommitResult {
  added: PanelAlbumPhoto[];
  failed: { photo_id: string; code: string }[];
  version: string;
  count: number;
}

export async function getPanelAlbum(panelId: string): Promise<PanelAlbum> {
  return (await apiFetch<PanelAlbum>(`/v3/panels/${encodeURIComponent(panelId)}/album`)).data;
}

export async function preparePanelPhotos(panelId: string, photos: { content_type: string; bytes: number }[]): Promise<PanelPhotoUpload[]> {
  return (await apiFetch<{ uploads: PanelPhotoUpload[] }>(`/v3/panels/${encodeURIComponent(panelId)}/photos:prepare`,
    { method: 'POST', body: { photos } })).data.uploads ?? [];
}

export async function commitPanelPhotos(panelId: string, photoIds: string[]): Promise<PanelCommitResult> {
  return (await apiFetch<PanelCommitResult>(`/v3/panels/${encodeURIComponent(panelId)}/photos:commit`,
    { method: 'POST', body: { photo_ids: photoIds } })).data;
}

export async function orderPanelPhotos(panelId: string, photoIds: string[]): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(panelId)}/photos:order`, { method: 'PUT', body: { photo_ids: photoIds } });
}

export async function deletePanelPhoto(panelId: string, photoId: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(panelId)}/photos/${encodeURIComponent(photoId)}`, { method: 'DELETE' });
}

/** 雲端給的網址可能是相對路徑（本機 /v3/dev/docs-upload、/v3/recordings/download）→ 接在 api 位址後；網址本身帶簽章、不帶權杖 */
export function resolvePanelUrl(u: string, apiBase: string = config.apiBase): string {
  return /^https?:\/\//i.test(u) ? u : apiBase.replace(/\/+$/, '') + u;
}

/** 直接 PUT 到 upload_url（Content-Type 與長度要跟 prepare 一致） */
export async function uploadPanelPhoto(uploadUrl: string, blob: Blob, contentType: string): Promise<void> {
  const res = await fetch(resolvePanelUrl(uploadUrl), { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
  if (!res.ok) throw new Error(`upload ${res.status}`);
}

/** 照片長邊：能力表 features.photo_long_edge；讀不到（分享者 403、還沒診斷）→ 2266 */
export async function panelPhotoLongEdge(panelId: string): Promise<number> {
  try {
    const v = await getPanelCaps(panelId);
    const n = Number((v.caps as { features?: { photo_long_edge?: number } } | null)?.features?.photo_long_edge);
    return n >= 720 && n <= 4096 ? n : DEFAULT_LONG_EDGE;
  } catch {
    return DEFAULT_LONG_EDGE;
  }
}

/** 還可以放幾張 */
export function photoRemaining(album: Pick<PanelAlbum, 'count' | 'quota'> | null): number {
  if (!album) return PANEL_PHOTO_QUOTA;
  return Math.max(0, (album.quota || PANEL_PHOTO_QUOTA) - album.count);
}

/** 能不能上傳：主人，或分享關係有 photo_upload（SD §5） */
export function canUploadPhotos(d: Pick<DeviceSummary, 'role' | 'permissions'>): boolean {
  return d.role === 'owner' || (d.permissions ?? []).includes('photo_upload');
}

/** 能不能刪這張：主人刪任何一張；分享者只能刪自己上傳的（SD §3 v1.3） */
export function canDeletePhoto(isOwner: boolean, photo: PanelAlbumPhoto, myUserId: string | null): boolean {
  return isOwner || (!!myUserId && photo.uploaded_by === myUserId);
}

/** 目前登入者的 user_id（access token 的 sub；只讀自己的宣告，不驗簽） */
export function currentUserId(token: string | null = tokenStore.get()): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.sub === 'string' ? json.sub : null;
  } catch {
    return null;
  }
}

/** 拖曳排序：把 from 移到 to（回新陣列） */
export function movePhoto<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = list.slice();
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
}

/** 縮到長邊 ≤ longEdge 的 JPEG（HEIC 由瀏覽器解碼：Safari 可，其他瀏覽器解不開會丟錯 → 畫面提示換格式） */
export async function toPanelJpeg(file: Blob, longEdge: number): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, longEdge / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  const out = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', 0.9));
  if (!out) throw new Error('encode failed');
  return out;
}

export interface PanelTimeWindow { on: boolean; from: string; to: string }
export interface PanelDisplaySettings {
  interval_s: number;
  order: 'sequential' | 'shuffle';
  transition: 'fade' | 'none';
  sleep: PanelTimeWindow & { tap_wake_s: number };
  clock: { on: boolean };
  night: PanelTimeWindow & { ring_volume: number; ring: boolean; photos: boolean };
  ring: boolean;
}

/** SD §4 預設值（雲端沒存過的欄位也是這些） */
export const DEFAULT_DISPLAY: PanelDisplaySettings = {
  interval_s: 15,
  order: 'sequential',
  transition: 'fade',
  sleep: { on: true, from: '23:00', to: '07:00', tap_wake_s: 30 },
  clock: { on: true },
  night: { on: true, from: '22:00', to: '07:00', ring_volume: 0.3, ring: true, photos: true },
  ring: true,
};

/** GET settings 回應（顯示欄位攤平在頂層）→ 顯示設定；缺的用預設 */
export function parseDisplaySettings(raw: Record<string, unknown> | null | undefined): PanelDisplaySettings {
  const r = raw ?? {};
  const pick = <T extends object>(v: unknown, d: T): T => (v && typeof v === 'object' ? { ...d, ...(v as Partial<T>) } : d);
  return {
    interval_s: typeof r.interval_s === 'number' ? r.interval_s : DEFAULT_DISPLAY.interval_s,
    order: r.order === 'shuffle' ? 'shuffle' : 'sequential',
    transition: r.transition === 'none' ? 'none' : 'fade',
    sleep: pick(r.sleep, DEFAULT_DISPLAY.sleep),
    clock: pick(r.clock, DEFAULT_DISPLAY.clock),
    night: pick(r.night, DEFAULT_DISPLAY.night),
    ring: typeof r.ring === 'boolean' ? r.ring : DEFAULT_DISPLAY.ring,
  };
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** 送出前檢查（同雲端範圍）；回錯誤欄位清單，空 = 可送 */
export function validateDisplay(s: PanelDisplaySettings): string[] {
  const bad: string[] = [];
  if (!(s.interval_s >= 10 && s.interval_s <= 3600)) bad.push('interval_s');
  if (!HHMM.test(s.sleep.from) || !HHMM.test(s.sleep.to)) bad.push('sleep');
  if (!(s.sleep.tap_wake_s >= 5 && s.sleep.tap_wake_s <= 300)) bad.push('sleep.tap_wake_s');
  if (!HHMM.test(s.night.from) || !HHMM.test(s.night.to)) bad.push('night');
  if (!(s.night.ring_volume >= 0.1 && s.night.ring_volume <= 1)) bad.push('night.ring_volume');
  return bad;
}

export async function getPanelSettings(panelId: string): Promise<Record<string, unknown>> {
  return (await apiFetch<Record<string, unknown>>(`/v3/panels/${encodeURIComponent(panelId)}/settings`)).data;
}

/** 只限主人；巢狀物件雲端部分合併 */
export async function putPanelDisplay(panelId: string, s: PanelDisplaySettings): Promise<Record<string, unknown>> {
  return (await apiFetch<Record<string, unknown>>(`/v3/panels/${encodeURIComponent(panelId)}/settings`, { method: 'PUT', body: s })).data;
}

// ── P4 / P4b（docs/panel 05-ui-ux V-02 / V-06 / V-10 / V-11 / V-12 / V-15 / V-22；SD §9、§13 v1.4）──

/** V-02 待辦（GET /v3/panels/{id}/todo，雲端即時算） */
export interface PanelTodo {
  key: string;
  text_key: string;
  action_screen: string;
  dismissible: boolean;
  count?: number;
  names?: string[];
  /** home_paused：暫停到什麼時候（unix 秒） */
  until?: number;
}

export async function getPanelTodo(id: string): Promise<PanelTodo[]> {
  return (await apiFetch<PanelTodo[]>(`/v3/panels/${encodeURIComponent(id)}/todo`)).data ?? [];
}

/** 略過一項待辦：PUT settings {extra:{todo_dismissed:[…已略過, key]}} */
export async function dismissPanelTodo(id: string, key: string, already: string[]): Promise<void> {
  const list = Array.from(new Set([...already, key]));
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/settings`, { method: 'PUT', body: { extra: { todo_dismissed: list } } });
}

/** 雲端 text_key（panel.todo.no_doorbell）→ 網頁 i18n key（panels.todo.no_doorbell） */
export function todoI18nKey(item: Pick<PanelTodo, 'key'>): string {
  return `panels.todo.${item.key}`;
}

/** Android 面板「還不是主畫面」→ 叫面板再跳一次系統確認（docs/panel/03-sd.md §16.2） */
export async function askPanelHome(id: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/home:ask`, { method: 'POST', body: {} });
}

/** 待辦按鈕（雲端 key → 動作）：home_app → 再問一次；home_normal / home_paused → 回到面板模式 */
export function panelTodoAction(key: string): 'ask_home' | 'home_panel' | null {
  if (key === 'home_app') return 'ask_home';
  if (key === 'home_normal' || key === 'home_paused') return 'home_panel';
  return null;
}

// ── 平板主畫面模式（Android 面板；iPad 的 home = null 不顯示卡片）──

export type PanelHomeMode = 'panel' | 'normal' | 'paused';

/** GET /v3/panels/{id}/home 的 home 物件（只列觀看 App 用得到的欄位；時間 unix 秒） */
export interface PanelHomeInfo {
  is_home: boolean;
  /** panel = 平板按 Home 回面板；normal = 一般使用，面板不擋其他 App */
  mode: 'panel' | 'normal';
  /** 暫停到什麼時候（0 = 沒暫停） */
  paused_until: number;
  pin_unset?: boolean;
  keyguard_locked?: boolean;
  /** 已送出、面板還沒處理 */
  pending_mode?: { mode: PanelHomeMode; at: number };
}

export async function getPanelHome(id: string): Promise<PanelHomeInfo | null> {
  return (await apiFetch<{ home: PanelHomeInfo | null }>(`/v3/panels/${encodeURIComponent(id)}/home`)).data?.home ?? null;
}

/** 遠端切換：paused（暫停 10 分鐘）/ normal（恢復一般使用）/ panel（回到面板模式） */
export async function setPanelHomeMode(id: string, mode: PanelHomeMode): Promise<{ at: number; mode: string }> {
  return (await apiFetch<{ at: number; mode: string }>(`/v3/panels/${encodeURIComponent(id)}/home:mode`, { method: 'POST', body: { mode } })).data;
}

/** 卡片顯示哪一種：pending（已送出、等面板）> paused（暫停中）> normal（一般使用）> panel（面板模式） */
export function panelHomeCardState(h: PanelHomeInfo, nowS: number): 'pending' | 'paused' | 'normal' | 'panel' {
  if (h.pending_mode) return 'pending';
  if ((h.paused_until ?? 0) > nowS) return 'paused';
  return h.mode === 'normal' ? 'normal' : 'panel';
}

/** 暫停到幾點（HH:mm 本地時間；暫停只有 10 分鐘，不用日期） */
export function formatHm(unixS: number): string {
  const d = new Date(unixS * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export async function resumePanelPin(id: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(id)}/pin:resume`, { method: 'POST', body: {} });
}

/** V-06 門鈴與警報、開門設定（settings 子集） */
export interface PanelBellSettings {
  doorbells: string[];
  ring: boolean;
  alarms: string[];
  allow_door_open: boolean;
  pin_shuffle: boolean;
  control_window_s: number;
  todo_dismissed: string[];
  /** 面板上可以用的驗證方式（有效值；雲端沒存過 = ["pin","phone"]，10 §4.1 M1）。保留雲端給的其他值（例如 biometric） */
  unlock_methods: string[];
  /** 手機確認可不可以開門（預設 false，10 J4）；打開後還要手機在家裡網路 */
  phone_allows_door: boolean;
}

export function parseBellSettings(raw: Record<string, unknown> | null | undefined): PanelBellSettings {
  const r = raw ?? {};
  const extra = (r.extra ?? {}) as Record<string, unknown>;
  const arr = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const cw = Number(r.control_window_s);
  return {
    doorbells: arr(r.doorbells),
    ring: r.ring !== false,
    alarms: arr(r.alarms),
    allow_door_open: r.allow_door_open === true,
    pin_shuffle: r.pin_shuffle === true,
    control_window_s: Number.isFinite(cw) && cw >= 30 && cw <= 300 ? cw : 120,
    todo_dismissed: arr(extra.todo_dismissed ?? r.todo_dismissed),
    unlock_methods: Array.isArray(r.unlock_methods) && arr(r.unlock_methods).length ? arr(r.unlock_methods) : [...DEFAULT_UNLOCK_METHODS],
    phone_allows_door: r.phone_allows_door === true,
  };
}

// ── D2 手機確認：網頁只有開關與清單（docs/panel/10-phone-auth-scenes.md §16.1 D2、M4：網頁不能手機確認） ──

/** unlock_methods 沒存值 = PIN + 手機確認（10 M1） */
export const DEFAULT_UNLOCK_METHODS: readonly string[] = ['pin', 'phone'];

/** 驗證方式勾 / 取消一個；至少要留一個（雲端空陣列回 400），會變空就不動 */
export function toggleUnlockMethod(list: readonly string[], m: string): string[] {
  if (!list.includes(m)) return [...list, m];
  const next = list.filter((x) => x !== m);
  return next.length ? next : [...list];
}

/** 兩組驗證方式是不是一樣（不管順序）；一樣就不送，免得把預設值存成「主人改過」 */
export function sameMethods(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

/** V-11 可以幫面板確認的手機（GET /v3/me/confirm-keys；10 §2.4、§7.2） */
export interface ConfirmKey {
  key_id: string;
  platform: string;
  device_name: string;
  /** unix 秒 */
  created_at: number;
  /** 冷卻到（unix 秒）：這之前不能確認開門、看要 PIN 的攝影機、暫停攝影機 / 關偵測 */
  cooldown_until: number;
  last_used_at?: number;
}
export interface ConfirmKeyHistory {
  op: 'add' | 'delete' | 'delete_all' | string;
  key_id?: string;
  platform?: string;
  device_name?: string;
  /** delete_all：password_changed | account_deleted */
  reason?: string;
  at: number;
}
export interface ConfirmKeyList {
  keys: ConfirmKey[];
  history: ConfirmKeyHistory[];
}

export async function listConfirmKeys(): Promise<ConfirmKeyList> {
  const raw = (await apiFetch<unknown>('/v3/me/confirm-keys')).data;
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const keys = (Array.isArray(o.keys) ? o.keys : [])
    .map((k) => (k && typeof k === 'object' ? (k as Record<string, unknown>) : {}))
    .filter((k) => str(k.key_id))
    .map((k): ConfirmKey => ({
      key_id: str(k.key_id), platform: str(k.platform), device_name: str(k.device_name), created_at: num(k.created_at),
      cooldown_until: num(k.cooldown_until), ...(num(k.last_used_at) ? { last_used_at: num(k.last_used_at) } : {}),
    }));
  const history = (Array.isArray(o.history) ? o.history : [])
    .map((h) => (h && typeof h === 'object' ? (h as Record<string, unknown>) : {}))
    .filter((h) => str(h.op))
    .map((h): ConfirmKeyHistory => ({
      op: str(h.op), key_id: str(h.key_id) || undefined, platform: str(h.platform) || undefined,
      device_name: str(h.device_name) || undefined, reason: str(h.reason) || undefined, at: num(h.at),
    }));
  return { keys, history };
}

/** DELETE /v3/me/confirm-keys/{key_id}（不存在也 204） */
export async function deleteConfirmKey(keyId: string): Promise<void> {
  await apiFetch(`/v3/me/confirm-keys/${encodeURIComponent(keyId)}`, { method: 'DELETE' });
}

/** 還在冷卻期（新登記 24 小時內） */
export function confirmKeyCooling(k: Pick<ConfirmKey, 'cooldown_until'>, nowS = Date.now() / 1000): boolean {
  return k.cooldown_until > nowS;
}

/** 紀錄一列的文案 key：加了 / 刪了 / 改密碼全刪 / 刪帳號全刪 */
export function confirmKeyHistoryKey(h: ConfirmKeyHistory): string {
  if (h.op === 'add') return 'confirm.hist_add';
  if (h.op === 'delete_all') return h.reason === 'account_deleted' ? 'confirm.hist_delete_account' : 'confirm.hist_delete_all';
  return 'confirm.hist_delete';
}

export async function putPanelSettings(id: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  return (await apiFetch<Record<string, unknown>>(`/v3/panels/${encodeURIComponent(id)}/settings`, { method: 'PUT', body })).data;
}

/** 主人的火災 / 煙霧裝置（0x8012） */
export function alarmIdsOf(devices: DeviceSummary[]): string[] {
  return devices
    .filter((d) => d.role === 'owner' && !isPanelDevice(d) && (d.capability_effective ?? []).some((c) => (c.cluster ?? '').toLowerCase() === '0x8012'))
    .map((d) => d.device_id);
}

/** V-11 我的面板 PIN */
export interface MyPanelPin {
  set: boolean;
  /** panel_device_id → 鎖到（unix 秒） */
  locks?: Record<string, number>;
  /** owner_user_id → 暫停到（unix 秒） */
  owner_pauses?: Record<string, number>;
}

export async function getMyPanelPin(): Promise<MyPanelPin> {
  return (await apiFetch<MyPanelPin>('/v3/me/panel-pin')).data;
}
export async function putMyPanelPin(pin: string): Promise<void> {
  await apiFetch('/v3/me/panel-pin', { method: 'PUT', body: { pin } });
}
export async function deleteMyPanelPin(): Promise<void> {
  await apiFetch('/v3/me/panel-pin', { method: 'DELETE' });
}

// ---- 打電話時顯示的名字（John 2026-10-22）----
// 主人設 caller_name（面板設定，≤ 20 字，"" = 用面板名稱）；每個人可以另外設自己怎麼稱呼這台（my-name，只影響自己的手機）。

export const CALLER_NAME_MAX = 20;

/** 面板設定裡的 caller_name（沒有欄位 → ""） */
export function callerNameOf(settings: Record<string, unknown> | null | undefined): string {
  const v = settings?.caller_name;
  return typeof v === 'string' ? v : '';
}

export async function putPanelCallerName(panelId: string, name: string): Promise<Record<string, unknown>> {
  return putPanelSettings(panelId, { caller_name: name.trim() });
}

/** GET /v3/panels/{id}/my-name */
export interface PanelMyName {
  my_name: string;
  caller_name: string;
  panel_name: string;
}

export function parsePanelMyName(raw: unknown): PanelMyName {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return { my_name: str(o.my_name), caller_name: str(o.caller_name), panel_name: str(o.panel_name) };
}

export async function getPanelMyName(panelId: string): Promise<PanelMyName> {
  return parsePanelMyName((await apiFetch<unknown>(`/v3/panels/${encodeURIComponent(panelId)}/my-name`)).data);
}

/** "" = 清掉（退回 caller_name / 面板名稱） */
export async function putPanelMyName(panelId: string, name: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(panelId)}/my-name`, { method: 'PUT', body: { name: name.trim() } });
}

/** GET /v3/me/panels 一筆（帳號頁「我怎麼稱呼這台」列表） */
export interface MyPanelEntry extends PanelMyName {
  panel_device_id: string;
}

export function parseMyPanels(raw: unknown): MyPanelEntry[] {
  const list = (raw as { panels?: unknown } | null)?.panels;
  if (!Array.isArray(list)) return [];
  return list.flatMap((v) => {
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    if (typeof o.panel_device_id !== 'string' || !o.panel_device_id) return [];
    return [{ panel_device_id: o.panel_device_id, ...parsePanelMyName(o) }];
  });
}

export async function listMyPanels(): Promise<MyPanelEntry[]> {
  return parseMyPanels((await apiFetch<unknown>('/v3/me/panels')).data);
}

/** GET /v3/panels/{id}/family 一位（主人看；auto_answer = 這位打來時面板響 10 秒後自動接通） */
export interface PanelFamilyMember {
  user_id: string;
  name: string;
  auto_answer: boolean;
}

export function parsePanelFamily(raw: unknown): PanelFamilyMember[] {
  const list = (raw as { family?: unknown } | null)?.family;
  if (!Array.isArray(list)) return [];
  return list.flatMap((v) => {
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    if (typeof o.user_id !== 'string' || !o.user_id) return [];
    return [{ user_id: o.user_id, name: typeof o.name === 'string' && o.name.trim() ? o.name.trim() : o.user_id, auto_answer: o.auto_answer === true }];
  });
}

export async function getPanelFamily(panelId: string): Promise<PanelFamilyMember[]> {
  return parsePanelFamily((await apiFetch<unknown>(`/v3/panels/${encodeURIComponent(panelId)}/family`)).data);
}

/** 自動接聽名單 = 開著的人（照清單順序） */
export function autoAnswerUsers(family: PanelFamilyMember[]): string[] {
  return family.filter((m) => m.auto_answer).map((m) => m.user_id);
}

export async function putPanelAutoAnswer(panelId: string, userIds: string[]): Promise<Record<string, unknown>> {
  return putPanelSettings(panelId, { auto_answer_users: userIds });
}

/** 來電 / 求救標題用的名字：我的叫法 → 主人設的 caller_name → 面板名稱 */
export function panelCallName(p: { my_name?: string; caller_name?: string; panel_name: string }): string {
  return (p.my_name ?? '').trim() || (p.caller_name ?? '').trim() || p.panel_name.trim();
}

/** 客戶端先擋：6–12 位數字。弱碼以雲端 PIN_WEAK 為準，這裡只先擋最明顯的（全同、連號）。 */
export function pinFormatError(pin: string): 'format' | 'weak' | null {
  if (!/^\d{6,12}$/.test(pin)) return 'format';
  if (/^(\d)\1+$/.test(pin)) return 'weak';
  const asc = '01234567890123456789';
  const desc = '98765432109876543210';
  if (asc.includes(pin) || desc.includes(pin)) return 'weak';
  return null;
}

/** 現在還有效的鎖 / 暫停（秒） */
export function activeUntil(m: Record<string, number> | undefined, nowS: number = Date.now() / 1000): [string, number][] {
  return Object.entries(m ?? {}).filter(([, t]) => t > nowS);
}

/** V-10 沒帳號家人 */
export interface PanelMember {
  member_id: string;
  nickname: string;
  perms: string[];
  panels: string[];
  disabled?: boolean;
  pin_set: boolean;
  locks?: Record<string, number>;
  owner_pause?: number;
  created_at?: number;
}

export async function listPanelMembers(): Promise<PanelMember[]> {
  return (await apiFetch<PanelMember[]>('/v3/panel-members')).data ?? [];
}
export async function createPanelMember(nickname: string, perms: string[], panels: string[]): Promise<PanelMember> {
  return (await apiFetch<PanelMember>('/v3/panel-members', { method: 'POST', body: { nickname, perms, panels } })).data;
}
export async function updatePanelMember(id: string, patch: Partial<Pick<PanelMember, 'nickname' | 'perms' | 'panels' | 'disabled'>>): Promise<void> {
  await apiFetch(`/v3/panel-members/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}
export async function deletePanelMember(id: string): Promise<void> {
  await apiFetch(`/v3/panel-members/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function resetPanelMemberPin(id: string): Promise<void> {
  await apiFetch(`/v3/panel-members/${encodeURIComponent(id)}/pin`, { method: 'DELETE' });
}

export interface PanelSetupCode { setup_code: string; member_id: string; expires_at: number }
export async function createSetupCode(panelId: string, memberId: string): Promise<PanelSetupCode> {
  return (await apiFetch<PanelSetupCode>(`/v3/panels/${encodeURIComponent(panelId)}/setup-codes`, { method: 'POST', body: { member_id: memberId } })).data;
}

/** 設定碼顯示成 XXXX-XXXX */
export function formatSetupCode(c: string): string {
  const s = c.replace(/[^0-9A-Z]/gi, '').toUpperCase();
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4)}` : s;
}

/** 有帳號家人的面板 PIN 停用（在我的面板上） */
export interface PanelPinBlock { owner_user_id?: string; user_id: string; created_at?: number }
export async function listPanelPinBlocks(): Promise<PanelPinBlock[]> {
  return (await apiFetch<PanelPinBlock[]>('/v3/me/panel-pin-blocks')).data ?? [];
}
export async function setPanelPinBlock(userId: string, on: boolean): Promise<void> {
  await apiFetch(`/v3/me/panel-pin-blocks/${encodeURIComponent(userId)}`, { method: on ? 'PUT' : 'DELETE', ...(on ? { body: {} } : {}) });
}

/** 有帳號家人：主人各台裝置上的直接分享對象（面板帶來的關係不算；同一人合併） */
export interface ShareLikeRow {
  user_id?: string | null;
  invitee_email?: string;
  invitee_display_name?: string | null;
  relation_status?: string;
  granted_via?: string;
  read_only?: boolean;
}
export function accountFamily(rowsByDevice: ShareLikeRow[][]): { user_id: string; name: string }[] {
  const m = new Map<string, string>();
  for (const rows of rowsByDevice) {
    for (const r of rows) {
      if (!r.user_id || r.relation_status !== 'active' || r.granted_via === 'panel' || r.read_only) continue;
      if (!m.has(r.user_id)) m.set(r.user_id, r.invitee_display_name || r.invitee_email || r.user_id);
    }
  }
  return [...m.entries()].map(([user_id, name]) => ({ user_id, name }));
}

/** V-15 面板相關操作紀錄（door / alarm / panel 類別）→ i18n key + 參數 */
export interface PanelActivityRow {
  action: string;
  category: string;
  device_id?: string;
  actor: string;
  actor_is_me?: boolean;
  meta?: Record<string, unknown>;
  ts_us: number;
}
export function panelActivityKey(r: PanelActivityRow): { key: string; params: Record<string, unknown> } {
  const m = r.meta ?? {};
  // 帳號層級的 meta 可能沒有 nickname / level（例如 setup_code 只帶 member）→ 補空字串，顯示端再收掉空括號
  const params: Record<string, unknown> = { who: r.actor_is_me ? '' : r.actor, nickname: '', level: '', ...m };
  switch (r.action) {
    case 'door.open':
      return { key: `panels.act.door_${String(m.result ?? 'unknown')}`, params };
    case 'panel.unlock':
      // 手機確認（10 §10）：發票 / 被拒 / 手機上按了［不是我］
      if (m.result === 'phone_denied') return { key: 'auth.phone.denied', params };
      if (m.method === 'phone' && m.result !== 'denied') return { key: 'confirm.log_phone', params };
      return { key: m.result === 'denied' ? 'panels.act.unlock_denied' : 'panels.act.unlock', params };
    default:
      if (r.action.startsWith('alarm.')) return { key: 'panels.act.alarm', params };
      return { key: `panels.act.${r.action.replace(/^panel\./, '').replace(/\./g, '_')}`, params };
  }
}

/** 紀錄 meta 的 level → 文案 key（confirm.action.*，10 §17）；認不得的等級 = undefined（照原值顯示） */
export function panelActivityLevelKey(level: unknown): string | undefined {
  return typeof level === 'string' && ['door', 'view', 'general', 'layout'].includes(level) ? `confirm.action.${level}` : undefined;
}

export async function getPanelActivity(category: 'door' | 'alarm' | 'panel', deviceId?: string): Promise<PanelActivityRow[]> {
  const now = Date.now() * 1000;
  return (await apiFetch<PanelActivityRow[]>('/v3/me/activity', {
    query: { scope: 'my_devices', category, from: now - 30 * 86_400_000_000, to: now + 60_000_000, limit: 200, devices: deviceId },
  })).data ?? [];
}

/** V-22 快速設定預設值：門鈴全勾；攝影機標室外 / 門口的預勾插播（沒標當室內、不預勾）；夜間 22:00–07:00 */
export interface QuickSetup {
  doorbells: string[];
  cameras: { device_id: string; location: '' | 'indoor' | 'outdoor' | 'door' }[];
  interrupt: string[];
  night: { on: boolean; from: string; to: string };
}
export function quickSetupDefaults(devices: DeviceSummary[]): QuickSetup {
  const owned = devices.filter((d) => d.role === 'owner' && !isPanelDevice(d));
  const doorbells = doorbellIdsOf(devices);
  const cams = owned
    .filter((d) => isCameraClass(d) && !doorbells.includes(d.device_id))
    .map((d) => ({ device_id: d.device_id, location: (['indoor', 'outdoor', 'door'].includes(d.camera_location ?? '') ? d.camera_location : '') as QuickSetup['cameras'][number]['location'] }));
  return {
    doorbells,
    cameras: cams,
    interrupt: cams.filter((c) => c.location === 'outdoor' || c.location === 'door').map((c) => c.device_id),
    night: { on: true, from: '22:00', to: '07:00' },
  };
}

export async function setCameraLocation(deviceId: string, location: string): Promise<void> {
  await apiFetch(`/v3/devices/${encodeURIComponent(deviceId)}/location-tag`, { method: 'PUT', body: { location } });
}

/** 按完成才寫入（V-22）：門鈴、插播攝影機、夜間 */
export async function applyQuickSetup(panelId: string, q: QuickSetup, withNight = true): Promise<void> {
  await putPanelSettings(panelId, quickSetupBody(q, withNight));
}

export function quickSetupBody(q: QuickSetup, withNight = true): Record<string, unknown> {
  const body: Record<string, unknown> = { doorbells: q.doorbells, interrupt: { cameras: q.interrupt } };
  if (withNight) body.night = { on: q.night.on, from: q.night.from, to: q.night.to };
  return body;
}

// ── P5（docs/panel 05-ui-ux V-05 插播設定與統計；SD §4 interrupt、§6、§7 末 v1.5）──

export const INTERRUPT_KINDS = ['person', 'vehicle', 'animal', 'package', 'motion'] as const;
export const INTERRUPT_COOLDOWNS = [60, 300, 600, 1800] as const;

export interface PanelInterruptSettings {
  cameras: string[];
  kinds: string[];
  quiet: { on: boolean; from: string; to: string };
  cooldown_s: number;
  sound: boolean;
  /** 也插播還沒確認的事件（verdicts 含 unverified；預設關，John 2026-10-14） */
  includeUnverified: boolean;
  /** 雲端依攝影機位置算（沒標 / 室內 → true）；唯讀 */
  live_requires_unlock: Record<string, boolean>;
}

export function parseInterruptSettings(raw: Record<string, unknown> | null | undefined): PanelInterruptSettings {
  const r = raw ?? {};
  const o = (r.interrupt ?? {}) as Record<string, unknown>;
  const arr = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null);
  const q = (o.quiet ?? null) as Record<string, unknown> | null;
  const hhmm = (v: unknown, d: string) => (typeof v === 'string' && /^\d{2}:\d{2}$/.test(v) ? v : d);
  const cd = Number(o.cooldown_s);
  const lru = (r.live_requires_unlock ?? {}) as Record<string, unknown>;
  return {
    cameras: arr(o.cameras) ?? [],
    kinds: arr(o.kinds) ?? ['person'],
    quiet: q && typeof q.from === 'string' ? { on: q.on !== false, from: hhmm(q.from, '22:00'), to: hhmm(q.to, '07:00') } : { on: false, from: '22:00', to: '07:00' },
    cooldown_s: Number.isFinite(cd) && cd > 0 ? cd : 300,
    sound: o.sound === true,
    includeUnverified: (arr(o.verdicts) ?? ['confirmed']).includes('unverified'),
    live_requires_unlock: Object.fromEntries(Object.entries(lru).map(([k, v]) => [k, v === true])),
  };
}

/** PUT settings 的 interrupt 物件（SD §4；quiet 關掉 = null） */
export function interruptBody(s: PanelInterruptSettings): Record<string, unknown> {
  return {
    interrupt: {
      cameras: s.cameras,
      kinds: s.kinds,
      quiet: s.quiet.on ? { from: s.quiet.from, to: s.quiet.to } : null,
      cooldown_s: s.cooldown_s,
      sound: s.sound,
      verdicts: s.includeUnverified ? ['confirmed', 'unverified'] : ['confirmed'],
    },
  };
}

/** 主人的攝影機（可插播；不含面板、門鈴另在 V-06 選） */
export function interruptCamerasOf(devices: DeviceSummary[]): DeviceSummary[] {
  const bells = new Set(doorbellIdsOf(devices));
  return devices.filter((d) => d.role === 'owner' && !isPanelDevice(d) && isCameraClass(d) && !bells.has(d.device_id));
}

/** 在面板看即時影像要不要先驗證：沒標 / 室內 → 要（R2-PU-7，同雲端算法） */
export function liveNeedsUnlock(location: string | undefined): boolean {
  return location !== 'outdoor' && location !== 'door';
}

// ---- 攝影機「要不要 PIN」（John 2026-10-21：家人只看到一個開關，不再露出室內 / 室外 / 門口）----
// 底層照舊寫 PUT /v3/devices/{id}/location-tag：要 PIN = indoor、直接看 = outdoor（原本是 door 的保留 door）。

/** 在面板上看這台要不要 PIN（同 liveNeedsUnlock：沒標 / 室內 → 要） */
export function cameraRequiresPin(location: string | undefined): boolean {
  return liveNeedsUnlock(location);
}

/** 開關 → 要寫的 location-tag；已標 door 的「直接看」不改掉它 */
export function cameraPinTag(requirePin: boolean, current: string | undefined): 'indoor' | 'outdoor' | 'door' {
  if (requirePin) return 'indoor';
  return current === 'door' ? 'door' : 'outdoor';
}

/** 從沒標過（空 / 不認得）→ 第一次放進面板時當場問一次 */
export function cameraUntagged(location: string | undefined): boolean {
  return !location || !['indoor', 'outdoor', 'door'].includes(location);
}

/** GET /v3/panels/{id}/interrupt-stats（近 7 天；只限主人） */
export interface PanelInterruptStats {
  from: string;
  to: string;
  total: number;
  by_day: Record<string, number>;
  cameras: Record<string, Record<string, number>>;
  /** 近 7 天 5 秒內被關掉的總數（舊雲端沒有 → 0） */
  closed_lt5s: number;
  /** 每台：插播次數、5 秒內關掉次數（依次數排序；舊雲端沒有 → 空） */
  by_camera: InterruptCameraClose[];
  /** 近 7 天 ≥ 10 次且 5 秒內關掉 ≥ 50% 才有：建議取消這台（SD §6 第 5 點） */
  suggest: InterruptCameraClose | null;
}

export interface InterruptCameraClose {
  camera_id: string;
  name: string;
  shown: number;
  closed_lt5s: number;
}

function cameraCloseOf(v: unknown): InterruptCameraClose | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (typeof o.camera_id !== 'string' || !o.camera_id) return null;
  const n = (x: unknown) => (Number.isFinite(Number(x)) ? Number(x) : 0);
  return { camera_id: o.camera_id, name: typeof o.name === 'string' ? o.name : '', shown: n(o.shown), closed_lt5s: n(o.closed_lt5s) };
}

export function parseInterruptStats(raw: Record<string, unknown> | null | undefined): PanelInterruptStats {
  const d = (raw ?? {}) as Partial<Record<keyof PanelInterruptStats, unknown>>;
  const byCam = Array.isArray(d.by_camera) ? d.by_camera.map(cameraCloseOf).filter((x): x is InterruptCameraClose => x !== null) : [];
  return {
    from: typeof d.from === 'string' ? d.from : '',
    to: typeof d.to === 'string' ? d.to : '',
    total: Number(d.total) || 0,
    by_day: (d.by_day as Record<string, number>) ?? {},
    cameras: (d.cameras as Record<string, Record<string, number>>) ?? {},
    closed_lt5s: Number(d.closed_lt5s) || 0,
    by_camera: byCam,
    suggest: cameraCloseOf(d.suggest),
  };
}

export async function getInterruptStats(panelId: string): Promise<PanelInterruptStats> {
  const d = (await apiFetch<Record<string, unknown>>(`/v3/panels/${encodeURIComponent(panelId)}/interrupt-stats`)).data;
  return parseInterruptStats(d);
}

/** ISO 週（「這週不再提示」用）：例 2026-W42 */
export function isoWeekKey(now: Date): string {
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y = d.getUTCFullYear();
  const week = Math.ceil(((d.getTime() - Date.UTC(y, 0, 1)) / 86400000 + 1) / 7);
  return `${y}-W${String(week).padStart(2, '0')}`;
}

const SUGGEST_KEY = 'ls.panel.intrSuggestDismissed';
type KV = Pick<Storage, 'getItem' | 'setItem'>;
function safeStore(): KV | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** ［不用］：這個面板 × 這台攝影機這週不再提示（只存本機） */
export function dismissSuggest(panelId: string, cameraId: string, now = new Date(), store: KV | null = safeStore()): void {
  if (!store) return;
  try {
    const m = JSON.parse(store.getItem(SUGGEST_KEY) ?? '{}') as Record<string, string>;
    m[`${panelId}|${cameraId}`] = isoWeekKey(now);
    store.setItem(SUGGEST_KEY, JSON.stringify(m));
  } catch { /* 本機存不了就算了 */ }
}

export function suggestDismissed(panelId: string, cameraId: string, now = new Date(), store: KV | null = safeStore()): boolean {
  if (!store) return false;
  try {
    const m = JSON.parse(store.getItem(SUGGEST_KEY) ?? '{}') as Record<string, string>;
    return m[`${panelId}|${cameraId}`] === isoWeekKey(now);
  } catch {
    return false;
  }
}

/** 每台合計，次數多的在前 */
export function statsByCamera(st: PanelInterruptStats): { camera_id: string; total: number; kinds: Record<string, number> }[] {
  return Object.entries(st.cameras)
    .map(([camera_id, kinds]) => ({ camera_id, kinds, total: Object.values(kinds).reduce((a, b) => a + b, 0) }))
    .sort((a, b) => b.total - a.total || a.camera_id.localeCompare(b.camera_id));
}

// ---- L1：小工具版面的歷史與還原（docs/panel 07-dashboard §5.4、§7；只限主人）----

/** 版面歷史一列（GET /v3/panels/{id}/layout/history；雲端保留最近 5 版，新的在前） */
export interface PanelLayoutVersion {
  version: string;
  /** panel / app / default / restore（舊雲端或沒見過的值照原字串，顯示端退回「其他」） */
  from: string;
  by: string;
  /** unix 秒 */
  at: number;
  widgets: number;
  is_current: boolean;
  /** 版本號是內容雜湊：還原到舊版後，舊的那列跟目前同一個 version（雲端兩列都回 is_current）→ 只有最新那列算目前，其他標「跟目前一樣」、不給還原 */
  same_as_current: boolean;
}

export const LAYOUT_FROMS = ['panel', 'app', 'default', 'restore'] as const;

export function parseLayoutHistory(raw: unknown): PanelLayoutVersion[] {
  if (!Array.isArray(raw)) return [];
  const rows = raw.flatMap((v): PanelLayoutVersion[] => {
    if (!v || typeof v !== 'object') return [];
    const o = v as Record<string, unknown>;
    if (typeof o.version !== 'string' || !o.version) return [];
    return [{
      version: o.version,
      from: typeof o.from === 'string' ? o.from : '',
      by: typeof o.by === 'string' ? o.by : '',
      at: Number(o.at) || 0,
      widgets: Number(o.widgets) || 0,
      is_current: o.is_current === true,
      same_as_current: false,
    }];
  });
  // 雲端已是新的在前；保險再依時間排一次（同一秒維持雲端順序）
  rows.sort((a, b) => b.at - a.at);
  const cur = rows.find((r) => r.is_current)?.version;
  let seen = false;
  return rows.map((r) => {
    const same = r.version === cur;
    const first = same && !seen;
    if (same) seen = true;
    return { ...r, is_current: first, same_as_current: same && !first };
  });
}

/** 來源 → i18n key（在面板上 / 在 App 上 / 預設 / 還原） */
export function layoutFromKey(from: string): string {
  return (LAYOUT_FROMS as readonly string[]).includes(from) ? `panels.layout.from_${from}` : 'panels.layout.from_other';
}

/** 有沒有「上一版」可還原（歷史裡有內容跟目前不同的版本；雲端 previous = 第一個版本號不是目前的） */
export function hasPreviousLayout(rows: PanelLayoutVersion[]): boolean {
  return rows.some((r) => !r.is_current && !r.same_as_current);
}

export async function getLayoutHistory(panelId: string): Promise<PanelLayoutVersion[]> {
  return parseLayoutHistory((await apiFetch<unknown>(`/v3/panels/${encodeURIComponent(panelId)}/layout/history`)).data);
}

/** POST layout:restore {version}；`previous` = 還原上一版。404 LAYOUT_VERSION_NOT_FOUND（那一版已不在）、403（不是主人） */
export async function restoreLayout(panelId: string, version: string): Promise<void> {
  await apiFetch(`/v3/panels/${encodeURIComponent(panelId)}/layout:restore`, { method: 'POST', body: { version } });
}

/** 推播 panel.layout_changed 點進來 → 面板頁並捲到版面卡片 */
export const LAYOUT_CARD_ANCHOR = 'layout';

// ---- L2：時鐘小工具的天氣地點（docs/panel 07-dashboard §3.4；設定只限主人）----

/** settings.weather：auto = 依面板對外 IP 的城市；manual = 主人選的城市 */
export interface PanelWeatherSetting {
  mode: 'auto' | 'manual';
  name?: string;
  lat?: number;
  lon?: number;
}

/** GET /v3/weather/places 一筆 */
export interface WeatherPlace {
  name: string;
  admin: string;
  country: string;
  lat: number;
  lon: number;
}

/** GET /v3/panels/{id}/weather */
export interface PanelWeatherNow {
  place: string;
  /** manual / ip */
  source: string;
  temp_c: number;
  hi_c: number;
  lo_c: number;
  /** WMO 天氣代碼 */
  code: number;
  is_day: boolean;
  /** unix 秒 */
  updated_at: number;
}

/** 雲端城市名上限 40 字（以字元算，不切半個字） */
export const WEATHER_NAME_MAX = 40;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** settings 回應 → 天氣地點；manual 缺城市或座標 → 當 auto（雲端不會存這種，保險） */
export function parseWeatherSetting(raw: Record<string, unknown> | null | undefined): PanelWeatherSetting {
  const w = (raw?.weather ?? null) as Record<string, unknown> | null;
  if (!w || typeof w !== 'object' || w.mode !== 'manual') return { mode: 'auto' };
  const lat = num(w.lat);
  const lon = num(w.lon);
  if (typeof w.name !== 'string' || !w.name || lat === null || lon === null) return { mode: 'auto' };
  return { mode: 'manual', name: w.name, lat, lon };
}

export function parseWeatherPlaces(raw: unknown): WeatherPlace[] {
  const list = (raw as { places?: unknown } | null)?.places;
  if (!Array.isArray(list)) return [];
  return list.flatMap((v): WeatherPlace[] => {
    if (!v || typeof v !== 'object') return [];
    const o = v as Record<string, unknown>;
    const lat = num(o.lat);
    const lon = num(o.lon);
    if (typeof o.name !== 'string' || !o.name || lat === null || lon === null) return [];
    // 來源的別名會寫成「臺灣省 or 台灣省」→ 只取第一個
    const admin = typeof o.admin === 'string' ? o.admin.split(' or ')[0].trim() : '';
    return [{ name: o.name, admin, country: typeof o.country === 'string' ? o.country : '', lat, lon }];
  });
}

/** 搜尋結果第二行：省 / 州 · 國家（空的、跟城市名一樣的不列） */
export function placeDetail(p: WeatherPlace): string {
  return [p.admin, p.country].filter((s, i, a) => s && s !== p.name && a.indexOf(s) === i).join(' · ');
}

/** 選了搜尋結果 → 要存的設定（城市名超過 40 字就截短） */
export function manualWeatherFrom(p: WeatherPlace): PanelWeatherSetting {
  return { mode: 'manual', name: Array.from(p.name).slice(0, WEATHER_NAME_MAX).join(''), lat: p.lat, lon: p.lon };
}

export function parseWeatherNow(raw: unknown): PanelWeatherNow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const temp = num(o.temp_c);
  if (temp === null) return null;
  return {
    place: typeof o.place === 'string' ? o.place : '',
    source: typeof o.source === 'string' ? o.source : '',
    temp_c: temp,
    hi_c: num(o.hi_c) ?? temp,
    lo_c: num(o.lo_c) ?? temp,
    code: num(o.code) ?? 0,
    is_day: o.is_day !== false,
    updated_at: num(o.updated_at) ?? 0,
  };
}

/** 天氣讀取 / 設定失敗 → 顯示哪一句（i18n key 的最後一段） */
export type WeatherErr = 'noPlace' | 'unavailable' | 'invalid' | 'forbidden' | 'network' | 'generic';

export function weatherErr(e: unknown): WeatherErr {
  if (!(e instanceof ApiError)) return 'network'; // fetch 本身丟的（離線、連不上）
  if (e.code === 'WEATHER_NO_PLACE') return 'noPlace';
  if (e.code === 'NETWORK_UNAVAILABLE') return 'network';
  if (e.status === 503) return 'unavailable';
  if (e.status === 422) return 'invalid';
  if (e.status === 403) return 'forbidden';
  return 'generic';
}

export async function searchWeatherPlaces(q: string): Promise<WeatherPlace[]> {
  return parseWeatherPlaces((await apiFetch<unknown>('/v3/weather/places', { query: { q } })).data);
}

/** 404 WEATHER_NO_PLACE（auto 但查不到城市）、503（天氣來源掛了 / 關掉） */
export async function getPanelWeather(panelId: string): Promise<PanelWeatherNow | null> {
  return parseWeatherNow((await apiFetch<unknown>(`/v3/panels/${encodeURIComponent(panelId)}/weather`)).data);
}

/** PUT settings {weather}（其他欄位不變）→ 回存好的天氣地點；422 INVALID_SETTING */
export async function putPanelWeather(panelId: string, w: PanelWeatherSetting): Promise<PanelWeatherSetting> {
  return parseWeatherSetting(await putPanelSettings(panelId, { weather: w }));
}

// ---- L3：緊急求救（docs/panel 07-dashboard §3.5、D11；雲端 10_surface_app_api「L3 緊急求救」）----
// 推播還沒接 → App 開著時每 15 秒輪詢 GET /v3/me/panel-sos（panelSos.ts）。誤按的雲端不會列。

/** GET /v3/me/panel-sos 一筆 */
export interface PanelSos {
  sos_id: string;
  panel_device_id: string;
  panel_name: string;
  /** 打電話時顯示的名字（主人設的；舊雲端沒有 → 不帶，退回 panel_name） */
  caller_name?: string;
  state: 'sent' | 'acked';
  /** unix 秒 */
  at: number;
  pushes: number;
  /** 已按［我知道了］的家人名字（先按的在前） */
  acked_by: string[];
}

export const SOS_POLL_MS = 15_000;

export function parsePanelSos(raw: unknown): PanelSos | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.sos_id !== 'string' || !o.sos_id || typeof o.panel_device_id !== 'string') return null;
  if (o.state !== 'sent' && o.state !== 'acked') return null; // mistake 不會列；保險
  return {
    sos_id: o.sos_id,
    panel_device_id: o.panel_device_id,
    panel_name: typeof o.panel_name === 'string' ? o.panel_name : '',
    ...(typeof o.caller_name === 'string' && o.caller_name.trim() ? { caller_name: o.caller_name.trim() } : {}),
    state: o.state,
    at: num(o.at) ?? 0,
    pushes: num(o.pushes) ?? 0,
    acked_by: Array.isArray(o.acked_by) ? o.acked_by.filter((n): n is string => typeof n === 'string' && !!n) : [],
  };
}

export function parsePanelSosList(raw: unknown): PanelSos[] {
  const list = (raw as { sos?: unknown } | null)?.sos;
  if (!Array.isArray(list)) return [];
  return list.flatMap((v) => {
    const s = parsePanelSos(v);
    return s ? [s] : [];
  });
}

export async function listMyPanelSos(): Promise<PanelSos[]> {
  return parsePanelSosList((await apiFetch<unknown>('/v3/me/panel-sos')).data);
}

/** POST :ack → 同一筆（state=acked、acked_by 多了自己）。404 SOS_NOT_FOUND、409 SOS_STATE（已標成誤按） */
export async function ackPanelSos(sosId: string): Promise<PanelSos | null> {
  return parsePanelSos((await apiFetch<unknown>('/v3/me/panel-sos:ack', { method: 'POST', body: { sos_id: sosId } })).data);
}

/** 畫面上怎麼呈現一筆：
 *  - alert = 全螢幕紅色提醒（還沒人回覆、我也沒按過、沒先收成橫幅）
 *  - mine = 我已回覆（小橫幅「你已回覆；〇〇 已收到」）
 *  - others = 別人已回覆、我還沒按（小橫幅 +［我也知道了］）
 *  - pending = 還沒人回覆，但我按了［看面板］先收起（小橫幅 +［我知道了］） */
export type SosShow = 'alert' | 'mine' | 'others' | 'pending';

/** 已有人回覆的求救，送出滿這麼久（秒）就不再掛小橫幅（D13，John 2026-10-16：橫幅永遠不會消失） */
export const SOS_BANNER_AUTOHIDE_S = 30 * 60;

/** dismissed = 「你已回覆」按了［收起］的；nowSec > 0 時，已有人回覆且送出滿 30 分鐘的也不掛（還沒人回覆的照樣提醒） */
export function classifySos(list: PanelSos[], mine: ReadonlySet<string>, minimized: ReadonlySet<string>,
  dismissed: ReadonlySet<string> = new Set(), nowSec = 0): { alert: PanelSos | null; banners: Array<{ sos: PanelSos; show: Exclude<SosShow, 'alert'> }> } {
  let alert: PanelSos | null = null;
  const banners: Array<{ sos: PanelSos; show: Exclude<SosShow, 'alert'> }> = [];
  for (const s of list) {
    const answered = mine.has(s.sos_id) || s.state === 'acked';
    if (answered && (dismissed.has(s.sos_id) || (nowSec > 0 && nowSec - s.at >= SOS_BANNER_AUTOHIDE_S))) continue;
    if (mine.has(s.sos_id)) banners.push({ sos: s, show: 'mine' });
    else if (s.state === 'acked') banners.push({ sos: s, show: 'others' });
    else if (!alert && !minimized.has(s.sos_id)) alert = s; // 新的在前 → 一次只蓋最新一筆，其他先當橫幅
    else banners.push({ sos: s, show: 'pending' });
  }
  return { alert, banners };
}

/** 求救時間 → 「剛剛 / N 分鐘前 / N 小時前」用的 [key, n]（雲端只留 2 小時） */
export function sosAgo(atSec: number, nowMs: number = Date.now()): ['justNow' | 'minutesAgo' | 'hoursAgo', number] {
  const min = Math.floor((nowMs / 1000 - atSec) / 60);
  if (min < 1) return ['justNow', 0];
  if (min < 60) return ['minutesAgo', min];
  return ['hoursAgo', Math.floor(min / 60)];
}

/** ack 失敗 → 這筆還要不要留著：404 / 409 = 雲端已經沒有或已是誤按 → 拿掉 */
export function sosAckGone(e: unknown): boolean {
  return e instanceof ApiError && (e.code === 'SOS_NOT_FOUND' || e.code === 'SOS_STATE' || e.status === 404 || e.status === 409);
}
